import { type InputHTMLAttributes, forwardRef, useId } from 'react'

/**
 * Mobile-friendly text input with a floating label.
 *
 * Three things we get for free here:
 *   - **16px font-size** — the iOS rule against zoom-on-focus.
 *     Anything smaller and Safari zooms the viewport when the user
 *     taps the field, then they have to pinch back out.
 *   - **48px touch target** for the input itself.
 *   - **Visible error state** — when `error` is provided, the
 *     border + helper text turn red. Helper text always lives in
 *     the same DOM slot, so the label doesn't jump when the error
 *     appears (no layout shift).
 */

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string
  error?: string | null
  hint?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, id, className, ...rest },
  ref,
) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const helperId = `${inputId}-helper`

  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={inputId}
        className="text-sm font-medium text-text"
      >
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error !== undefined && error !== null && error !== ''}
        aria-describedby={hint !== undefined || error !== undefined ? helperId : undefined}
        className={[
          'min-h-[3rem] w-full rounded-xl border-2 px-4 py-2',
          'text-base bg-surface text-text placeholder-text-muted',
          'transition-colors duration-150',
          'focus:outline-none focus:ring-0',
          error !== undefined && error !== null && error !== ''
            ? 'border-danger focus:border-danger'
            : 'border-surface-border focus:border-brand',
          className ?? '',
        ].join(' ')}
        {...rest}
      />
      {/* Single helper slot — error trumps hint to avoid noise. */}
      <p
        id={helperId}
        className={[
          'min-h-[1.25rem] text-sm',
          error !== undefined && error !== null && error !== ''
            ? 'text-danger'
            : 'text-text-muted',
        ].join(' ')}
      >
        {error ?? hint ?? ''}
      </p>
    </div>
  )
})
